import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TUser } from "../../shared/types/user.type";
import { TrainingSetting } from "../../shared/types/trainingSetting.type";
import { TPlatformConfiguration } from "../../shared/types/platformConfiguration";

type UserInitialState = {
  isAuthenticated: boolean;
  user: TUser | null;
  isUserLoading: boolean;
  restrictedMode: boolean;
  initialSpeedUnit?: string;
  initialUnitSystem?: string;
  restrictedExam?: string;
  platformConfigurations?: TPlatformConfiguration[];
  maintenance: boolean;
};

const initialState: UserInitialState = {
  maintenance: false,
  isAuthenticated: false,
  user: null,
  isUserLoading: false,
  restrictedMode: false,
  restrictedExam: undefined,
  platformConfigurations: [
    {
      key: "alert_percentage_good_cardio_treshold_bad",
      value: "80",
    },
    {
      key: "force_graphs_unit_time_if_good_gps_percentage_is_below",
      value: "50",
    },
  ],
};

export const userReducer = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAuth: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setUser: (state, action: PayloadAction<TUser | null>) => {
      state.user = action.payload;
    },

    // 1. Update a single field dynamically: dispatch(updateUserField({ field: 'name', value: 'John' }))
    updateUserField: <K extends keyof TUser>(
      state: UserInitialState,
      action: PayloadAction<{ field: K; value: TUser[K] }>,
    ) => {
      if (state.user) {
        state.user[action.payload.field] = action.payload.value;
      }
    },

    // 2. Update single or multiple fields at once: dispatch(updateUserFields({ name: 'John', email: 'john@example.com' }))
    updateUserFields: (state, action: PayloadAction<Partial<TUser>>) => {
      if (state.user) {
        Object.assign(state.user, action.payload);
      }
    },

    setPlatformConfigurations: (
      state,
      action: PayloadAction<TPlatformConfiguration[]>,
    ) => {
      state.platformConfigurations = action.payload;
    },
    setIsUserLoading: (state, action: PayloadAction<boolean>) => {
      state.isUserLoading = action.payload;
    },
    updateUserSettings: (state, action: PayloadAction<TrainingSetting>) => {
      if (state.user) {
        state.user.training_settings = action.payload;
      }
    },
    logout: (state) => {
      Object.assign(state, initialState);
    },
    setInitialUnitSystem: (
      state,
      action: PayloadAction<"metric" | "imperial">,
    ) => {
      state.initialUnitSystem = action.payload;
    },
    setInitialSpeedUnit: (state, action: PayloadAction<string>) => {
      state.initialSpeedUnit = action.payload;
    },
    setRestrictedMode: (state, action: PayloadAction<boolean>) => {
      state.restrictedMode = action.payload;
    },
    setRestrictedExam: (state, action: PayloadAction<string | undefined>) => {
      state.restrictedExam = action.payload;
    },
    setMaintenance: (state, action: PayloadAction<boolean>) => {
      state.maintenance = action.payload;
    },
  },
});

export const {
  logout,
  setAuth,
  setUser,
  updateUserField,
  updateUserFields,
  setIsUserLoading,
  updateUserSettings,
  setInitialUnitSystem,
  setInitialSpeedUnit,
  setRestrictedMode,
  setRestrictedExam,
  setPlatformConfigurations,
  setMaintenance,
} = userReducer.actions;

export default userReducer.reducer;
